<template>
    <div class="mt-6">
        <!-- Live summary display at the top -->
        <div v-if="allergenSummary" class="mb-6 rounded-md bg-green-50 p-4">
            <div class="flex">
                <div class="flex-shrink-0">
                    <svg class="h-5 w-5 text-green-400" viewBox="0 0 20 20" fill="currentColor">
                        <path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clip-rule="evenodd" />
                    </svg>
                </div>
                <div class="ml-3">
                    <p class="text-sm font-medium text-green-800">
                        Will be listed as: 
                        <span v-if="containsList.length">
                            <span class="font-semibold">Contains</span> – {{ containsList.join(', ') }}
                        </span>
                        <span v-if="containsList.length && mayContainList.length">; </span>
                        <span v-if="mayContainList.length">
                            <span class="font-semibold">May contain</span> – {{ mayContainList.join(', ') }}
                        </span>
                    </p>
                </div>
            </div>
        </div>

        <!-- Dietary tags section -->
        <h3 class="block text-sm font-medium leading-5 text-gray-700">
            Dietary tags
        </h3>
        <div class="mt-3 mb-6">
            <div class="flex flex-wrap gap-2">
                <div v-for="tag in dietaryTagsOnly" :key="tag.value" class="mb-2">
                    <span
                        @click="toggleProductTag(tag)"
                        :class="{
                            'bg-indigo-100 text-indigo-800 ring-1 ring-inset ring-indigo-600/20': product.tags && product.tags.includes(tag.value),
                            'bg-gray-100 text-gray-800 ring-1 ring-inset ring-gray-500/20': !product.tags || !product.tags.includes(tag.value)
                        }"
                        class="inline-flex cursor-pointer items-center rounded-md px-2 py-1 text-sm font-medium"
                    >
                        {{ tag.label }}
                        <span v-if="product.tags && product.tags.includes(tag.value)" class="ml-1 text-indigo-600">
                            <svg class="h-4 w-4" viewBox="0 0 20 20" fill="currentColor">
                                <path fill-rule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clip-rule="evenodd" />
                            </svg>
                        </span>
                    </span>
                </div>
                

            </div>
        </div>

        <!-- Flags section -->
        <h3 class="block text-sm font-medium leading-5 text-gray-700">
            Flags
        </h3>
        <div class="mt-3 mb-6">
            <div class="flex flex-wrap gap-2">
                <div v-for="tag in flagTags" :key="tag.value" class="mb-2">
                    <span
                        @click="toggleProductTag(tag)"
                        :class="{
                            'bg-indigo-100 text-indigo-800 ring-1 ring-inset ring-indigo-600/20': product.tags && product.tags.includes(tag.value),
                            'bg-gray-100 text-gray-800 ring-1 ring-inset ring-gray-500/20': !product.tags || !product.tags.includes(tag.value)
                        }"
                        class="inline-flex cursor-pointer items-center rounded-md px-2 py-1 text-sm font-medium"
                    >
                        {{ tag.label }}
                        <span v-if="product.tags && product.tags.includes(tag.value)" class="ml-1 text-indigo-600">
                            <svg class="h-4 w-4" viewBox="0 0 20 20" fill="currentColor">
                                <path fill-rule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clip-rule="evenodd" />
                            </svg>
                        </span>
                    </span>
                </div>
            </div>
        </div>

        <!-- Allergens heading -->
        <h3 class="block text-sm font-medium leading-5 text-gray-700">
            Allergens
        </h3>

        <!-- Search box and bulk actions -->
        <div class="mt-3 mb-4 flex space-x-2">
            <div class="relative flex-grow">
                <div class="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3">
                    <svg class="h-5 w-5 text-gray-400" viewBox="0 0 20 20" fill="currentColor">
                        <path fill-rule="evenodd" d="M8 4a4 4 0 100 8 4 4 0 000-8zM2 8a6 6 0 1110.89 3.476l4.817 4.817a1 1 0 01-1.414 1.414l-4.816-4.816A6 6 0 012 8z" clip-rule="evenodd" />
                    </svg>
                </div>
                <input
                    type="text"
                    v-model="searchQuery"
                    class="block w-full rounded-md border-gray-300 pl-10 pr-10 text-sm focus:border-indigo-500 focus:ring-indigo-500"
                    placeholder="Search allergens..."
                />
                <div 
                    v-if="searchQuery" 
                    class="absolute inset-y-0 right-0 flex cursor-pointer items-center pr-3"
                    @click="searchQuery = ''"
                >
                    <svg class="h-5 w-5 text-gray-400 hover:text-gray-500" viewBox="0 0 20 20" fill="currentColor">
                        <path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clip-rule="evenodd" />
                    </svg>
                </div>
            </div>
            <button
                @click="markAllFree"
                class="inline-flex items-center rounded-md border border-gray-300 bg-white px-3 py-2 text-sm font-medium leading-4 text-gray-700 shadow-sm hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2"
            >
                Mark all Free
            </button>
        </div>

        <!-- Allergens list with 3-state control -->
        <div class="rounded-md border border-gray-200 p-4">
            <div v-for="tag in filteredAllergens" :key="tag.value" class="mb-4 flex items-center justify-between">
                <span class="text-sm font-medium text-gray-700">{{ tag.label }}</span>
                <div class="flex space-x-2">
                    <label
                        :class="[
                            'inline-flex cursor-pointer items-center rounded-md px-3 py-1 text-sm font-medium',
                            getAllergenState(tag.value) === 'contains' 
                                ? 'bg-red-100 text-red-800 ring-1 ring-inset ring-red-600/20' 
                                : 'bg-white text-gray-600 ring-1 ring-inset ring-gray-300 hover:bg-gray-50'
                        ]"
                    >
                        <input
                            type="radio"
                            :name="`allergen-${tag.value}`"
                            :value="'contains'"
                            :checked="getAllergenState(tag.value) === 'contains'"
                            @change="setAllergenState(tag.value, 'contains')"
                            class="mr-1 h-4 w-4 border-gray-300 text-red-600 focus:ring-red-500"
                        />
                        Contains
                    </label>
                    <label
                        :class="[
                            'inline-flex cursor-pointer items-center rounded-md px-3 py-1 text-sm font-medium', 
                            getAllergenState(tag.value) === 'may-contain'
                                ? 'bg-amber-100 text-amber-800 ring-1 ring-inset ring-amber-600/20'
                                : 'bg-white text-gray-600 ring-1 ring-inset ring-gray-300 hover:bg-gray-50'
                        ]"
                    >
                        <input
                            type="radio"
                            :name="`allergen-${tag.value}`"
                            :value="'may-contain'"
                            :checked="getAllergenState(tag.value) === 'may-contain'"
                            @change="setAllergenState(tag.value, 'may-contain')"
                            class="mr-1 h-4 w-4 border-gray-300 text-amber-600 focus:ring-amber-500"
                        />
                        May contain
                    </label>
                    <label
                        :class="[
                            'inline-flex cursor-pointer items-center rounded-md px-3 py-1 text-sm font-medium',
                            getAllergenState(tag.value) === 'free'
                                ? 'bg-gray-100 text-gray-700 ring-1 ring-inset ring-gray-500/20'
                                : 'bg-white text-gray-600 ring-1 ring-inset ring-gray-300 hover:bg-gray-50'
                        ]"
                    >
                        <input
                            type="radio"
                            :name="`allergen-${tag.value}`"
                            :value="'free'"
                            :checked="getAllergenState(tag.value) === 'free'"
                            @change="setAllergenState(tag.value, 'free')"
                            class="mr-1 h-4 w-4 border-gray-300 text-gray-600 focus:ring-gray-500"
                        />
                        Free
                    </label>
                </div>
            </div>
        </div>

        <div
            @click="showNutrition = !showNutrition"
            class="mt-6 flex w-full cursor-pointer select-none items-center justify-between"
        >
            <span class="text-sm font-medium leading-5 text-gray-700">
                Nutrition
            </span>
            <span class="text-sm font-medium text-gray-700">
                <svg
                    class="h-6 w-6 transition duration-300"
                    :class="{ 'rotate-180 transform': showNutrition }"
                    fill="currentColor"
                    viewBox="0 0 20 20"
                    xmlns="http://www.w3.org/2000/svg"
                >
                    <path
                        fill-rule="evenodd"
                        d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z"
                        clip-rule="evenodd"
                    ></path>
                </svg>
            </span>
        </div>
        <div
            v-show="showNutrition"
            class="mt-3 rounded-md border-2 border-dashed border-gray-300 bg-gray-50 p-2 sm:p-4"
        >
            <div>
                <div class="grid grid-cols-6 gap-6">
                    <div class="col-span-6 sm:col-span-3">
                        <label
                            for="calories"
                            class="block text-sm font-medium text-gray-700"
                            >Calories (kcal)</label
                        >
                        <div class="relative mt-1 rounded-md shadow-sm">
                            <input
                                type="number"
                                v-model.number="product.nutrition.calories"
                                name="calories"
                                id="calories"
                                class="block w-full rounded-md border-gray-300 pr-16 focus:border-indigo-500 focus:ring-indigo-500 sm:pr-14 sm:text-sm"
                                placeholder=""
                            />
                            <div
                                class="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-3"
                            >
                                <span class="text-gray-500 sm:text-sm">
                                    kcal
                                </span>
                            </div>
                        </div>
                    </div>

                    <div class="col-span-6 sm:col-span-3">
                        <label
                            for="salt"
                            class="block text-sm font-medium text-gray-700"
                            >Salt</label
                        >
                        <div class="relative mt-1 rounded-md shadow-sm">
                            <input
                                type="number"
                                v-model.number="product.nutrition.salt"
                                name="salt"
                                id="salt"
                                class="block w-full rounded-md border-gray-300 pr-16 focus:border-indigo-500 focus:ring-indigo-500 sm:pr-14 sm:text-sm"
                                placeholder=""
                            />
                            <div
                                class="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-3"
                            >
                                <span class="text-gray-500 sm:text-sm">
                                    grams
                                </span>
                            </div>
                        </div>
                    </div>

                    <div class="col-span-6 sm:col-span-3">
                        <label
                            for="carbohydrates"
                            class="block text-sm font-medium text-gray-700"
                            >Carbohydrates</label
                        >
                        <div class="relative mt-1 rounded-md shadow-sm">
                            <input
                                type="number"
                                v-model.number="product.nutrition.carbs"
                                name="carbohydrates"
                                id="carbohydrates"
                                class="block w-full rounded-md border-gray-300 pr-16 focus:border-indigo-500 focus:ring-indigo-500 sm:pr-14 sm:text-sm"
                                placeholder=""
                            />
                            <div
                                class="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-3"
                            >
                                <span class="text-gray-500 sm:text-sm">
                                    grams
                                </span>
                            </div>
                        </div>
                    </div>

                    <div class="col-span-6 sm:col-span-3">
                        <label
                            for="carbohydrates-sugar"
                            class="block text-sm font-medium text-gray-700"
                            >Carbohydrates (sugars)</label
                        >
                        <div class="relative mt-1 rounded-md shadow-sm">
                            <input
                                type="number"
                                v-model.number="product.nutrition.carbsSugars"
                                name="carbohydrates-sugar"
                                id="carbohydrates-sugar"
                                class="block w-full rounded-md border-gray-300 pr-16 focus:border-indigo-500 focus:ring-indigo-500 sm:pr-14 sm:text-sm"
                                placeholder=""
                            />
                            <div
                                class="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-3"
                            >
                                <span class="text-gray-500 sm:text-sm">
                                    grams
                                </span>
                            </div>
                        </div>
                    </div>

                    <div class="col-span-6 sm:col-span-3">
                        <label
                            for="fats"
                            class="block text-sm font-medium text-gray-700"
                            >Fats</label
                        >
                        <div class="relative mt-1 rounded-md shadow-sm">
                            <input
                                type="number"
                                v-model.number="product.nutrition.fats"
                                name="fats"
                                id="fats"
                                class="block w-full rounded-md border-gray-300 pr-16 focus:border-indigo-500 focus:ring-indigo-500 sm:pr-14 sm:text-sm"
                                placeholder=""
                            />
                            <div
                                class="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-3"
                            >
                                <span class="text-gray-500 sm:text-sm">
                                    grams
                                </span>
                            </div>
                        </div>
                    </div>

                    <div class="col-span-6 sm:col-span-3">
                        <label
                            for="fats-saturates"
                            class="block text-sm font-medium text-gray-700"
                            >Fats (saturates)</label
                        >
                        <div class="relative mt-1 rounded-md shadow-sm">
                            <input
                                type="number"
                                v-model.number="product.nutrition.fatSaturates"
                                name="fats-saturates"
                                id="fats-saturates"
                                class="block w-full rounded-md border-gray-300 pr-16 focus:border-indigo-500 focus:ring-indigo-500 sm:pr-14 sm:text-sm"
                                placeholder=""
                            />
                            <div
                                class="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-3"
                            >
                                <span class="text-gray-500 sm:text-sm">
                                    grams
                                </span>
                            </div>
                        </div>
                    </div>

                    <div class="col-span-6 sm:col-span-3">
                        <label
                            for="fibre"
                            class="block text-sm font-medium text-gray-700"
                            >Fibre</label
                        >
                        <div class="relative mt-1 rounded-md shadow-sm">
                            <input
                                type="number"
                                v-model.number="product.nutrition.fibre"
                                name="fibre"
                                id="fibre"
                                class="block w-full rounded-md border-gray-300 pr-16 focus:border-indigo-500 focus:ring-indigo-500 sm:pr-14 sm:text-sm"
                                placeholder=""
                            />
                            <div
                                class="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-3"
                            >
                                <span class="text-gray-500 sm:text-sm">
                                    grams
                                </span>
                            </div>
                        </div>
                    </div>

                    <div class="col-span-6 sm:col-span-3">
                        <label
                            for="protein"
                            class="block text-sm font-medium text-gray-700"
                            >Protein</label
                        >
                        <div class="relative mt-1 rounded-md shadow-sm">
                            <input
                                v-model.number="product.nutrition.protein"
                                type="number"
                                name="protein"
                                id="protein"
                                class="block w-full rounded-md border-gray-300 pr-16 focus:border-indigo-500 focus:ring-indigo-500 sm:pr-14 sm:text-sm"
                                placeholder=""
                            />
                            <div
                                class="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-3"
                            >
                                <span class="text-gray-500 sm:text-sm">
                                    grams
                                </span>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>

        <div
            class="mt-6 flex w-full cursor-pointer select-none items-center justify-between"
            @click="showIngredients = !showIngredients"
        >
            <span class="text-sm font-medium leading-5 text-gray-700">
                Ingredients
            </span>
            <span class="text-sm font-medium text-gray-700">
                <svg
                    class="h-6 w-6 transition duration-300"
                    :class="{ 'rotate-180 transform': showIngredients }"
                    fill="currentColor"
                    viewBox="0 0 20 20"
                    xmlns="http://www.w3.org/2000/svg"
                >
                    <path
                        fill-rule="evenodd"
                        d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z"
                        clip-rule="evenodd"
                    />
                </svg>
            </span>
        </div>

        <base-tags-input
            v-if="product.ingredients"
            v-show="showIngredients"
            v-model="product.ingredients"
            class="mt-3"
            :placeholder="product.ingredients.length ? '' : 'salt, flour'"
        />

        <div
            class="mt-5 justify-between sm:mt-6 sm:flex sm:w-auto"
            :class="{ spinner: isLoading }"
        >
            <span class="flex rounded-md shadow-sm">
                <button
                    @click="update"
                    :disabled="isLoading"
                    type="button"
                    class="focus:outline-none inline-flex w-full justify-center rounded-md border border-transparent bg-indigo-600 px-4 py-2 text-base font-medium leading-6 text-white shadow-sm transition duration-150 ease-in-out hover:bg-indigo-500 focus:border-indigo-700 focus:shadow-outline-indigo sm:text-sm sm:leading-5"
                >
                    Save changes
                </button>
            </span>
            <span
                class="mt-3 flex w-full rounded-md shadow-sm sm:ml-3 sm:mt-0 sm:w-auto"
            >
                <button
                    @click="close"
                    type="button"
                    class="focus:outline-none inline-flex w-full justify-center rounded-md border border-gray-300 bg-white px-4 py-2 text-base font-medium leading-6 text-gray-700 shadow-sm transition duration-150 ease-in-out hover:text-gray-500 focus:border-blue-300 focus:shadow-outline sm:text-sm sm:leading-5"
                >
                    Close
                </button>
            </span>
        </div>
    </div>
</template>

<script>
import { tags } from '@/helpers/productTags';

export default {
    name: 'ProductTagsSettings',
    props: {
        product: {
            type: Object,
            required: true
        }
    },
    data() {
        return {
            isLoading: false,
            showNutrition: false,
            showIngredients: false,
            searchQuery: ''
        };
    },
    mounted() {
        if (!this.product.tags) {
            this.product.tags = [];
        }
        if (!this.product.nutrition) {
            this.product.nutrition = {};
        }
    },
    computed: {
        // Get all allergen tags (values 100-999)
        allergenTags() {
            return tags.filter(tag => tag.value >= 100 && tag.value <= 999);
        },
        // Get all dietary tags (values 0-99 and 1000-1129)
        dietaryTags() {
            return tags.filter(tag => 
                (tag.value >= 0 && tag.value <= 99) || 
                (tag.value >= 1000 && tag.value <= 1129)
            );
        },
        // Get only dietary tags (values 1000-1129)
        dietaryTagsOnly() {
            return tags.filter(tag => tag.value >= 1000 && tag.value <= 1129);
        },
        // Get only flag tags (values 0-99)
        flagTags() {
            return tags.filter(tag => tag.value >= 0 && tag.value <= 99);
        },
        // Filter allergens based on search query
        filteredAllergens() {
            if (!this.searchQuery) return this.allergenTags;
            
            return this.allergenTags.filter(tag => 
                tag.label.toLowerCase().includes(this.searchQuery.toLowerCase())
            );
        },
        // Generate summary for the "contains" allergens
        containsList() {
            if (!this.product.tags) return [];
            return this.allergenTags
                .filter(tag => this.product.tags.includes(tag.value))
                .map(tag => tag.label);
        },
        // Generate summary for the "may contain" allergens
        mayContainList() {
            if (!this.product.tags) return [];
            return this.allergenTags
                .filter(tag => this.product.tags.includes(200000 + tag.value))
                .map(tag => tag.label);
        },
        // Combined summary for display
        allergenSummary() {
            return this.containsList.length > 0 || this.mayContainList.length > 0;
        }
    },
    methods: {
        close() {
            this.$emit('close');
        },
        update() {
            this.$emit('update');
        },
        // Get the current state of an allergen (contains, may-contain, or free)
        getAllergenState(tagValue) {
            if (!this.product.tags) return 'free';
            
            if (this.product.tags.includes(tagValue)) {
                return 'contains';
            } else if (this.product.tags.includes(200000 + tagValue)) {
                return 'may-contain';
            } else {
                return 'free';
            }
        },
        // Set the state of an allergen
        setAllergenState(tagValue, state) {
            // Initialize tags array if it doesn't exist
            if (!this.product.tags) {
                this.product.tags = [];
            }
            
            // Remove both variants first
            this.product.tags = this.product.tags.filter(
                tag => tag !== tagValue && tag !== (200000 + tagValue)
            );
            
            // Add the appropriate tag based on the state
            if (state === 'contains') {
                this.product.tags.push(tagValue);
            } else if (state === 'may-contain') {
                this.product.tags.push(200000 + tagValue);
            }
            // If 'free', we don't add any tag
        },
        // Mark all allergens as free
        markAllFree() {
            if (!this.product.tags) {
                this.product.tags = [];
                return;
            }
            
            this.allergenTags.forEach(tag => {
                this.product.tags = this.product.tags.filter(
                    t => t !== tag.value && t !== (200000 + tag.value)
                );
            });
        },
        // Toggle dietary tags (simple on/off)
        toggleProductTag(tag) {
            if (!this.product.tags) {
                this.product.tags = [];
                this.product.tags.push(tag.value);
                return;
            }
            
            if (this.product.tags.includes(tag.value)) {
                this.product.tags = this.product.tags.filter(
                    el => el !== tag.value
                );
            } else {
                this.product.tags.push(tag.value);
            }
        },

    }
};
</script>
